/**
 * RadioChoice renderer — big obvious buttons for each option.
 *
 * Options syntax (comma-separated):
 *   Plain:         "yes,no"                    → value=yes, label=yes
 *   Value:Label:   "yes:Yes please,no:No"      → value=yes, label="Yes please"
 * Backward-compatible with the plain form. The saved `value` (used by
 * downstream `trigger_when` conditions) is always the part before the colon.
 *
 * In-Runner editing (when statusKey + taskId + action.id are passed):
 *   ✎ next to the choice question       → edits params.label
 *   ✎ on each button                    → edits that option's LABEL only
 *   × on each button                    → removes that option
 *   + Add option                        → appends a new option
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Pencil, Check, X, Plus, Loader2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function parseOptions(text) {
  return (text || '').toString().split(',').map(s => s.trim()).filter(Boolean).map(seg => {
    const idx = seg.indexOf(':');
    if (idx < 0) return { value: seg, label: seg.replace(/_/g, ' ') };
    return { value: seg.slice(0, idx).trim(), label: seg.slice(idx + 1).trim() };
  });
}

// iter303ak-fix57 · Dotted-key lookup that mirrors WorkflowRunnerPage.readKey
// (flat "foo.bar" first, nested fallback). Kept local so this renderer stays
// standalone.
function readKey(data, key) {
  if (!data || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(data, key)) return data[key];
  const parts = key.split('.');
  let v = data;
  for (const p of parts) {
    if (v == null || typeof v !== 'object') return undefined;
    v = v[p];
  }
  return v;
}

// iter303ak-fix57 · Compute the recommended-option set from
// `recommend_from` (dotted key, or pipe-joined multi-key) +
// `recommend_map` (state → csv of option values). Returns null when
// no recommendations apply so the renderer can render everything
// as normal.
function computeRecommendedSet(params, capturedData) {
  const from = params.recommend_from;
  const map = params.recommend_map;
  if (!from || !map || typeof map !== 'object') return null;
  const keys = from.split('|').map(k => k.trim()).filter(Boolean);
  const state = keys.map(k => {
    const v = readKey(capturedData || {}, k);
    return v == null ? '' : String(v);
  }).join(':');
  const csv = map[state];
  if (csv == null) return null;
  const set = new Set(String(csv).split(',').map(s => s.trim()).filter(Boolean));
  return set.size ? set : null;
}

function serialiseOptions(items) {
  return items.map(o => {
    const autoLabel = o.value.replace(/_/g, ' ');
    // Only emit `value:label` when the label differs from the auto-derived form
    return o.label && o.label !== autoLabel ? `${o.value}:${o.label}` : o.value;
  }).join(',');
}

export default function RadioChoice({ action, value, onChange, onNext, onBack, canBack,
                                      statusKey, taskId, onTemplateChanged, capturedData }) {
  const params = action.params || {};
  const options = parseOptions(params.options);
  const recommendedSet = computeRecommendedSet(params, capturedData);
  // iter303ak-fix57 · Surcharge banner. Only surfaces when the runner's
  // captured pricing_tier resolves to `urgent`. Rendered on the report-
  // type picker (recommend_from includes appraisal.pricing_tier) so the
  // operator sees WHY the recommendations narrowed.
  const currentTier = readKey(capturedData || {}, 'appraisal.pricing_tier');
  const tierBanner = (() => {
    const usesTier = (params.recommend_from || '').includes('appraisal.pricing_tier');
    if (!usesTier) return null;
    if (currentTier === 'urgent')  return { tone: 'rose',    label: 'Urgent surcharge will apply on the quote.' };
    if (currentTier === 'casual')  return { tone: 'emerald', label: 'Casual tier — a discount (sometimes free) applies.' };
    if (currentTier === 'general') return { tone: 'slate',   label: 'General tier — standard pricing.' };
    return null;
  })();
  const [local, setLocal] = useState(value || '');
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(params.label || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Per-option edit state
  const [editingOptIdx, setEditingOptIdx] = useState(-1);
  const [optDraft, setOptDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addLabel, setAddLabel] = useState('');

  useEffect(() => { setLocal(value || ''); }, [action.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLabelDraft(params.label || ''); }, [params.label, action.id]);

  const canEdit = !!(statusKey && taskId && action.id);

  const patchAction = async (patch) => {
    setSaving(true); setErr('');
    try {
      await api.patch(`/status-workflows/workflow/${statusKey}/tasks/${taskId}/actions/${action.id}`, patch);
      if (onTemplateChanged) await onTemplateChanged();
      return true;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
      return false;
    } finally { setSaving(false); }
  };

  const saveLabel = async () => {
    if (await patchAction({ label: labelDraft.trim() })) setEditingLabel(false);
  };

  const saveOptionLabel = async (idx) => {
    const next = options.slice();
    next[idx] = { ...next[idx], label: optDraft.trim() };
    if (await patchAction({ params: { options: serialiseOptions(next) } })) setEditingOptIdx(-1);
  };

  const removeOption = async (idx) => {
    const next = options.slice(); next.splice(idx, 1);
    await patchAction({ params: { options: serialiseOptions(next) } });
  };

  const addOption = async () => {
    if (!addValue.trim()) return;
    const next = options.concat([{ value: addValue.trim(), label: (addLabel.trim() || addValue.trim().replace(/_/g, ' ')) }]);
    if (await patchAction({ params: { options: serialiseOptions(next) } })) {
      setAdding(false); setAddValue(''); setAddLabel('');
    }
  };

  return (
    <div>
      <div className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-purple-600 font-semibold mb-2 flex items-center gap-2">
          <span>🔘 Choice</span>
          {canEdit && !editingLabel && (
            <button onClick={() => setEditingLabel(true)} data-testid="wf-choice-edit-btn"
              title="Edit question script"
              className="text-purple-500 hover:text-purple-700 flex items-center gap-1">
              <Pencil size={10} /> edit
            </button>
          )}
        </div>
        {editingLabel ? (
          <div>
            <textarea value={labelDraft} onChange={e => setLabelDraft(e.target.value)}
              rows={2}
              data-testid="wf-choice-label-input"
              className="w-full text-base text-slate-800 leading-relaxed border border-purple-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-purple-300" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={saveLabel} disabled={saving || !labelDraft.trim()}
                data-testid="wf-choice-label-save"
                className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
              <button onClick={() => { setEditingLabel(false); setLabelDraft(params.label || ''); setErr(''); }}
                data-testid="wf-choice-label-cancel"
                className="px-3 py-1 text-slate-500 text-xs hover:text-slate-700 flex items-center gap-1">
                <X size={12} /> Cancel
              </button>
              {err && <span className="text-xs text-rose-600">{err}</span>}
            </div>
          </div>
        ) : (
          <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-choice-label">
            {params.label || '(no label set)'}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {tierBanner && (
          <div data-testid="wf-choice-tier-banner"
            className={`col-span-2 mb-1 px-3 py-2 border rounded text-xs font-medium
              ${tierBanner.tone === 'rose'    ? 'bg-rose-50 border-rose-200 text-rose-700' :
                tierBanner.tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                                                'bg-slate-50 border-slate-200 text-slate-600'}`}>
            {tierBanner.label}
          </div>
        )}
        {options.map((o, idx) => {
          const isEditingThis = canEdit && editingOptIdx === idx;
          const isRecommended = recommendedSet ? recommendedSet.has(o.value) : true;
          return (
            <div key={idx} className="group relative">
              {isEditingThis ? (
                <div className="p-3 border-2 border-purple-400 bg-purple-50 rounded">
                  <label className="text-[10px] uppercase text-purple-700 font-semibold">Button label</label>
                  <input value={optDraft} onChange={e => setOptDraft(e.target.value)}
                    autoFocus
                    data-testid={`wf-choice-opt-input-${idx}`}
                    className="w-full text-sm mt-1 px-2 py-1 border border-purple-300 rounded" />
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => saveOptionLabel(idx)} disabled={saving}
                      data-testid={`wf-choice-opt-save-${idx}`}
                      className="px-2 py-0.5 bg-purple-600 text-white text-[11px] rounded hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1">
                      {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
                    </button>
                    <button onClick={() => setEditingOptIdx(-1)}
                      data-testid={`wf-choice-opt-cancel-${idx}`}
                      className="px-2 py-0.5 text-slate-500 text-[11px] hover:text-slate-700 flex items-center gap-1">
                      <X size={10} /> Cancel
                    </button>
                    <span className="text-[10px] text-slate-400 ml-auto">value: <code>{o.value}</code></span>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => { setLocal(o.value); onChange(o.value); }}
                    data-testid={`wf-choice-${o.value}`}
                    title={!isRecommended ? 'Not typical for this urgency — click to override.' : undefined}
                    className={`w-full px-6 py-4 border-2 rounded font-semibold transition text-slate-700 relative
                      ${local === o.value
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : isRecommended
                          ? 'border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'
                          : 'border-dashed border-slate-200 bg-slate-50 text-slate-400 hover:text-slate-600 hover:border-slate-300 opacity-70'}`}>
                    {o.label}
                    {recommendedSet && !isRecommended && (
                      <span className="block text-[10px] uppercase tracking-widest text-slate-400 mt-1 font-normal">
                        Not typical
                      </span>
                    )}
                    {recommendedSet && isRecommended && local !== o.value && (
                      <span className="absolute top-1 left-1 text-[9px] uppercase tracking-widest text-emerald-600 font-semibold px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded">
                        Recommended
                      </span>
                    )}
                  </button>
                  {canEdit && (
                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button type="button"
                        onClick={() => { setEditingOptIdx(idx); setOptDraft(o.label); }}
                        data-testid={`wf-choice-opt-edit-${idx}`}
                        title="Edit label"
                        className="p-1 bg-white rounded shadow-sm border border-slate-200 text-slate-400 hover:text-purple-600">
                        <Pencil size={10} />
                      </button>
                      <button type="button"
                        onClick={() => removeOption(idx)}
                        data-testid={`wf-choice-opt-remove-${idx}`}
                        title="Remove option"
                        className="p-1 bg-white rounded shadow-sm border border-slate-200 text-slate-400 hover:text-rose-600">
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        {canEdit && !adding && (
          <button type="button" onClick={() => setAdding(true)}
            data-testid="wf-choice-opt-add-btn"
            className="col-span-2 px-6 py-2 border-2 border-dashed border-slate-300 rounded text-xs text-slate-500 hover:border-purple-300 hover:text-purple-700 flex items-center justify-center gap-1">
            <Plus size={12} /> Add option
          </button>
        )}
        {canEdit && adding && (
          <div className="col-span-2 p-3 border-2 border-dashed border-purple-300 bg-purple-50/40 rounded">
            <div className="flex items-center gap-2 flex-wrap">
              <input value={addValue} onChange={e => setAddValue(e.target.value)}
                placeholder="value_key"
                data-testid="wf-choice-opt-add-value"
                className="text-xs px-2 py-1 border border-slate-300 rounded font-mono w-32" />
              <input value={addLabel} onChange={e => setAddLabel(e.target.value)}
                placeholder="Display label"
                data-testid="wf-choice-opt-add-label"
                className="text-xs px-2 py-1 border border-slate-300 rounded flex-1 min-w-[10rem]" />
              <button type="button" onClick={addOption} disabled={saving || !addValue.trim()}
                data-testid="wf-choice-opt-add-save"
                className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
              </button>
              <button type="button" onClick={() => { setAdding(false); setAddValue(''); setAddLabel(''); }}
                className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              <b>value_key</b> = the internal key used by any downstream <code>trigger_when</code> conditions.
              <b>Display label</b> = what the operator sees on the button.
            </div>
          </div>
        )}
      </div>

      {params.write_to && (
        <div className="text-xs text-slate-400 mb-4">
          <span className="font-semibold text-slate-500">Writes to:</span> <code>{params.write_to}</code>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!local}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">Next →</button>
      </div>
    </div>
  );
}
